import React, { useMemo } from "react";
import { useObservable } from "micro-observables";
import { useNavigate } from "@core/routing/use-navigate";
import { useI18n } from "@core/i18n/i18n";
import { getStartedManager } from "@core/service-container";
import { analyticsService } from "@core/service-container";
import { AnalyticsCategory } from "@domain/analytics/analytics-types";
import { userStatusManager } from "@core/service-container";
import styled from "@emotion/styled";
import { RegistrationStatus } from "@domain/user/user";
import { GetStartedStepStatus } from "@domain/get-started/get-started";
import PatientAddIcon from "@assets/icons/patient-add.svg";
import { RoutesPath } from "@ui/navigation/routes";
import { OnboardingCard } from "@ui/pages/get-started/components/onboarding-card";
import { ArrowButton } from "@ui/pages/get-started/components/arrow-button";
import { ValidateAccountCard } from "@ui/pages/home/components/validate-account-card";

interface PatientsCardProps {
	className?: string;
}

export const PatientsCard: React.FC<PatientsCardProps> = ({ className }) => {
	const { format } = useI18n();
	const navigate = useNavigate();
	const ptRegistrationStatus = useObservable(userStatusManager.ptRegistrationStatus);
	const patientsStepStatus = useObservable(getStartedManager.patientsStepStatus);

	const { status, bottom } = useMemo(() => {
		switch (ptRegistrationStatus) {
			case RegistrationStatus.INCOMPLETE:
			case RegistrationStatus.PENDING:
				return {
					status: GetStartedStepStatus.Disabled,
					bottom: <StyledValidateAccountCard showForPendingPt={true} />,
				};
			default:
				return {
					status: patientsStepStatus,
					bottom: (
						<ArrowButton
							label={format("getStarted.patientsCard.ctaButton")}
							onClick={() => {
								analyticsService.trackEvent({
									category: AnalyticsCategory.GETSTARTED,
									action: "cardCTA",
									params: ["patients"],
								});
								navigate(RoutesPath.Home, {
									state: {
										openAddPatientModal: true,
									},
								});
							}}
						/>
					),
				};
		}
	}, [format, navigate, patientsStepStatus, ptRegistrationStatus]);

	return (
		<OnboardingCard
			className={className}
			status={status}
			icon={PatientAddIcon}
			mainHeadline={format("getStarted.patientsCard.mainHeadline")}
			secondaryHeadline={format("getStarted.patientsCard.secondaryHeadline")}
			description={format("getStarted.patientsCard.description")}
			bottom={bottom}
		/>
	);
};

const StyledValidateAccountCard = styled(ValidateAccountCard)`
	.alertContainer {
		flex-direction: column;
	}
`;
